import React from "react";
import PropTypes from "prop-types";
import { Field, ErrorMessage, getIn, useFormikContext } from "formik";
import classNames from "classnames";
import { useIntl } from "react-intl";

import { states } from "../states";
import DashedField from "./dashed-field";

// @todo defaultValue is not working for WebformElementNumber
const NumberField = ({ item, compositeIndex, compositeParent }) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();
  const intl = useIntl();

  const { invisible, visible, enabled, disabled, optional, required } = states(
    item["#states"],
    values
  );

  const identifier = compositeParent
    ? `${compositeParent}[${compositeIndex}][${item.id}]`
    : item.id;
  const errorMessage = intl.formatMessage({
    id: "webform.validation.invalid_number_no_exponent",
  });
  const showExponentError = () => {
    setFieldTouched(identifier, true, false);
    setFieldError(identifier, errorMessage);
  };
  const containsExponentCharacter = (value) =>
    value != null && String(value).match(/e/i);
  const blockExponentInput = (event) => {
    if (event.key === "e" || event.key === "E") {
      event.preventDefault();
      showExponentError();
    }
  };
  const blockExponentBeforeInput = (event) => {
    if (containsExponentCharacter(event.data)) {
      event.preventDefault();
      showExponentError();
    }
  };
  const blockExponentPaste = (event) => {
    const text = event.clipboardData?.getData("text");

    if (containsExponentCharacter(text)) {
      event.preventDefault();
      showExponentError();
    }
  };
  const handleNumberChange = (event) => {
    if (containsExponentCharacter(event.target.value)) {
      event.preventDefault();
      showExponentError();
      return;
    }

    handleChange(event);
  };
  const fieldError = getIn(errors, identifier);
  const fieldTouched = getIn(touched, identifier);

  return (
    <div
      className={classNames({
        "form-group": true,
        hidden: invisible || !visible,
      })}
      style={item["#flex"] ? { flex: item["#flex"] } : {}}
    >
      <label htmlFor={item.id} className="form-label">
        {item["#title"]}
        {(!!item["#required"] || required) && !optional && visible && (
          <span className="required">*</span>
        )}
      </label>
      <DashedField>
        <Field
          id={identifier}
          name={identifier}
          type="number"
          min={item["#min"]}
          max={item["#max"]}
          step={item["#step"]}
          className={classNames({
            "form-control": true,
            "is-invalid": fieldError && fieldTouched,
          })}
          onBeforeInput={blockExponentBeforeInput}
          onChange={handleNumberChange}
          onKeyDown={blockExponentInput}
          onPaste={blockExponentPaste}
          onBlur={handleBlur}
          value={
            compositeParent
              ? values[compositeParent][compositeIndex][item.id]
              : values[item.id]
          }
          required={(!!item["#required"] || required) && !optional && visible}
          disabled={!enabled || disabled || item["#disabled"]}
        />
      </DashedField>
      <ErrorMessage
        role="region"
        aria-live="polite"
        component="span"
        name={identifier}
        className="error-message"
      />
      {!!item["#description"] && (
        <small
          className="form-description text-muted form-text"
          dangerouslySetInnerHTML={{ __html: item["#description"] }}
        />
      )}
    </div>
  );
};

NumberField.propTypes = {
  item: PropTypes.shape({
    "#default_value": PropTypes.string,
    "#description": PropTypes.string,
    "#flex": PropTypes.number,
    "#maxlength": PropTypes.number,
    "#minlength": PropTypes.number,
    "#placeholder": PropTypes.string,
    "#required": PropTypes.bool,
    "#required_error": PropTypes.string,
    "#states": PropTypes.object,
    "#submit__label": PropTypes.string,
    "#title": PropTypes.string,
    "#type": PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    "#min": PropTypes.number,
    "#max": PropTypes.number,
    "#step": PropTypes.number,
    "#disabled": PropTypes.bool,
  }),
  compositeIndex: PropTypes.number,
  compositeParent: PropTypes.string,
};

export default NumberField;
